// App.jsx
import { RouterProvider } from 'react-router-dom';
import routes from "./routes/Router";
import { useTheme } from "./hooks/useTheme";
import Logo from "./layouts/Logo";
import { createAudit } from './features/audit/data/auditService';
import { SignalRProvider } from "./components/userLocks/SignalRProvider";

// Authenticator
import { Amplify } from 'aws-amplify';
import { Authenticator, Button, Heading, Text, View } from '@aws-amplify/ui-react';

import { Hub } from 'aws-amplify/utils';
import { signInWithRedirect, getCurrentUser } from 'aws-amplify/auth';

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useDispatch } from 'react-redux';
import { fetchAuthenticatedUser } from "./features/auth/authenticationSlice";
import { fetchUserPermissions } from "./data/permissions/permissionsSlice";
import { fetchOpportunityAccounts, fetchOpportunityUsers } from './data/opportunities/opportunitiesSlice';

import { useCallback, useEffect, useState } from 'react';

Amplify.configure({
    Auth: {
        Cognito: {
            userPoolId: process.env.REACT_APP_AWS_USER_POOLS_ID,
            userPoolClientId: process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID,
            signUpVerificationMethod: 'code',
            loginWith: {
                oauth: {
                    domain: process.env.REACT_APP_AWS_OAUTH_DOMAIN,
                    scopes: ["email", "openid", "profile", "aws.cognito.signin.user.admin"],
                    redirectSignIn: [process.env.REACT_APP_AWS_OAUTH_REDIRECT_URI],
                    redirectSignOut: [process.env.REACT_APP_AWS_OAUTH_REDIRECT_URI],
                    responseType: 'code'
                }
            }
        }
    }
});

export default function App() {
    const routing = routes();
    const theme = useTheme();
    const dispatch = useDispatch();

    const [myConfirmSignupShow, setMyConfirmSignUpShow] = useState(false);
    const [userGuid, setUserGuid] = useState(null);

    async function SSOLogin() {
        console.log("Starting Login");
        try {
            const target =
                window.location.pathname + window.location.search + window.location.hash;

            // stash for fallback
            sessionStorage.setItem("postLoginRedirect", target);

            await signInWithRedirect({
                provider: { custom: process.env.REACT_APP_AWS_IDP },
                // Amplify returns this value to you after sign-in
                customState: target,
            });
        } catch (error) {
            console.error("SAML Federated Sign-In Error:", error);
        }
    }

    // Authenticator UI customizations
    const components = {
        Header() {
            return (
                <div className="my-5">
                    <Logo className="mt-5" />
                </div>
            );
        },
        SignIn: {
            FormFields() {
                return null; // Hide default login fields
            },
            Footer() {
                return null;
            },
            Header() {
                return (
                    <>
                        <br /><br />
                        <form data-amplify-form style={{ paddingTop: '0', height: '100px', width: 'auto' }}>
                            <Button
                                className="amplify-button amplify-field-group__control amplify-button--outline amplify-button--fullwidth"
                                style={{ margin: '2rem 0' }}
                                onClick={() => SSOLogin()}
                            >
                                Login with SSO
                            </Button>
                        </form>
                    </>
                );
            },
        },
    };

    const userAuthenticated = () => {
        dispatch(fetchAuthenticatedUser());
        dispatch(fetchUserPermissions());
        dispatch(fetchOpportunityAccounts());
        dispatch(fetchOpportunityUsers());
    };

    // Hub listener that safely closes over the setters (and fixes the name mismatch)
    const authListener = useCallback(async (data) => {
        const evt = data?.payload?.event;

        // try to read from Hub payload first (preferred), else fallback to sessionStorage
        const stateFromHub = data?.payload?.data?.state; // where Amplify returns customState
        const saved = sessionStorage.getItem("postLoginRedirect");
        const redirectTo = stateFromHub || saved;

        const doPostLoginRedirect = () => {
            if (redirectTo && typeof redirectTo === "string") {
                sessionStorage.removeItem("postLoginRedirect");

                // Build an absolute URL (React Router won't react to replaceState)
                const absolute = redirectTo.startsWith("http")
                    ? redirectTo
                    : `${window.location.origin}${redirectTo.startsWith("/") ? "" : "/"
                    }${redirectTo}`;

                // Hard replace so the router loads the intended route
                window.location.replace(absolute);
            }
        };

        switch (evt) {
            case "signUp":
                setMyConfirmSignUpShow(true);
                console.log("user signed up");
                break;

            case "confirmSignUp":
            case "signInWithRedirect":
            case "signedIn":
                setMyConfirmSignUpShow(false);

                if (process.env.REACT_APP_USELOCALDATA === "false") {
                    await createAudit({ action: "Login", result: "Successful" });
                }
                if (data.payload.data?.userId) setUserGuid(data.payload.data.userId);

                // send them back where they intended
                doPostLoginRedirect();

                console.log("user confirmation successful");
                break;

            case "cognitoHostedUI":
                console.log("Authenticated...");
                if (data.payload.data?.userId) setUserGuid(data.payload.data.userId);

                // handle this event too
                doPostLoginRedirect();
                break;

            case "signIn_failure":
            case "cognitoHostedUI_failure":
                console.log("Error", data);
                break;

            case "signOut":
                setUserGuid(null);
                break;

            default:
                console.log("unknown event type: ", evt);
                break;
        }
    }, []);

    // Register Hub listener + restore session
    useEffect(() => {
        // Restore session on reload
        (async () => {
            try {
                const user = await getCurrentUser();
                if (user?.userId) {
                    setUserGuid(user.userId);
                }
            } catch {
                setUserGuid(null);
            }
        })();

        const removeListener = Hub.listen('auth', authListener);
        return () => {
            removeListener();
        };
    }, [authListener]);

    // Toast theme
    const themeName = theme.replace('-mode', '');

    return (
        <div className={theme}>
            <Authenticator.Provider>
                <View>
                    <Authenticator components={components} loginMechanisms={[]} hideSignUp>
                        {({ signOut, user }) => (
                            <main>
                                {userAuthenticated()}
                                <SignalRProvider userGuid={userGuid}>
                                    <RouterProvider router={routing} />
                                    <ToastContainer
                                        className="main-toast-container"
                                        position="top-right"
                                        theme={themeName}
                                        autoClose={4000}
                                    />
                                </SignalRProvider>
                            </main>
                        )}
                    </Authenticator>
                </View>
            </Authenticator.Provider>

            {myConfirmSignupShow ? (
                <div data-amplify-authenticator style={{ marginTop: '0rem' }}>
                    <div data-amplify-container>
                        <div data-amplify-router>
                            <div dir="ltr" data-orientation="horizontal">
                                <div data-amplify-router-content="">
                                    <div>
                                        <form data-amplify-form style={{ height: '480px', width: 'auto' }}>
                                            <Heading level={4}>Pending Signup Confirmation</Heading>
                                            <div style={{ height: '2rem' }}></div>
                                            <Text level={2}>
                                                Please contact an administrator so we can verify your account and get you all setup.
                                            </Text>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
